import { ArrowDown, ArrowUpRight } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { useI18n } from "@/lib/i18n";
import { HERO } from "@/constants/testIds";

export default function Hero() {
  const { t } = useI18n();

  return (
    <section
      id="home"
      data-testid={HERO.root}
      className="relative min-h-screen w-full bg-[#070707] overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 -left-32 w-[600px] h-[600px] bg-[#7a1a2d]/15 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#3a2a1a]/30 blur-[140px] rounded-full pointer-events-none" />

      {/* Grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-36 lg:pt-44 pb-24 grid lg:grid-cols-12 gap-12 items-center min-h-screen">
        {/* Left text */}
        <div className="lg:col-span-7 text-white">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-[#c5a572]" />
            <span className="text-[11px] tracking-[0.42em] text-[#c5a572] uppercase">
              {t.hero.eyebrow}
            </span>
          </div>
          <h1
            data-testid={HERO.title}
            className="font-serif text-[58px] sm:text-[78px] lg:text-[112px] leading-[0.95] tracking-tight"
          >
            <span className="block text-white/95">Stanimirović</span>
            <span className="block italic text-[#c5a572] mt-3 text-[26px] sm:text-[32px] lg:text-[40px] tracking-wide">
              Porodična vinarija
            </span>
          </h1>
          <p className="mt-10 max-w-xl text-white/65 text-[15px] sm:text-base leading-relaxed font-light">
            {t.hero.subtitle}
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-5">
            <a
              data-testid={HERO.ctaPrimary}
              href="#spotlight"
              className="group inline-flex items-center gap-3 bg-[#c5a572] hover:bg-[#d4b685] text-[#0a0a0a] px-7 py-4 rounded-full text-[12px] tracking-[0.28em] uppercase font-medium transition-all duration-300"
            >
              {t.hero.cta}
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a
              data-testid={HERO.ctaSecondary}
              href="#about"
              className="inline-flex items-center gap-3 text-white/75 hover:text-white border border-white/15 hover:border-white/40 px-7 py-4 rounded-full text-[12px] tracking-[0.28em] uppercase transition-all duration-300"
            >
              {t.hero.ctaSecondary}
            </a>
          </div>

          <div className="mt-20 flex items-center gap-3 text-white/40 text-[10px] tracking-[0.4em] uppercase">
            <ArrowDown size={14} className="animate-bounce" />
            <span>Scroll</span>
          </div>
        </div>

        {/* Right product image */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
            <div className="absolute inset-0 bg-gradient-to-t from-[#c5a572]/20 via-transparent to-transparent rounded-[2px] blur-2xl" />
            <img
              src={ASSETS.heroBottle}
              alt="Noćna Tajna — Porodična vinarija Stanimirović"
              className="relative w-full h-full object-cover rounded-[2px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]"
            />
            <div className="absolute -bottom-6 -left-6 hidden lg:flex flex-col bg-[#0a0a0a]/85 backdrop-blur-md border border-white/10 px-5 py-4">
              <span className="text-[10px] tracking-[0.32em] text-[#c5a572] uppercase">Vintage</span>
              <span className="font-serif text-2xl text-white mt-1">2025</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
